import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay';

import { Theme } from 'helpers/theme';
import CourseCard, {
  SizeOptions,
  Course
} from 'sharedComponents/Overview/CourseCard';
import Dropdown, { DropdownOption } from 'sharedComponents/core/Input/Dropdown';
import PageHeader from 'components/PageHeader';
import SelectButton from 'components/core/Input/SelectButton';
import CircleBorder from 'sharedComponents/core/CircleBorder';
import Paginator, { Page } from 'sharedComponents/Pagination/Paginator';
import Spacer from 'sharedComponents/core/Spacers/Spacer';
import { Router } from 'found';
import { CoursesPage_courses } from './__generated__/CoursesPage_courses.graphql';
import CourseInfo from 'components/Course';
import {
  ActiveDelegates,
  ExpiringSoonDelegates,
  HistoricalCourseTakers
} from 'components/Course/queries';
import { setBodyOverflow } from './helpers';
import { queries_ActiveDelegatesQueryResponse } from 'components/Course/__generated__/queries_ActiveDelegatesQuery.graphql';
import { queries_HistoricalDelegatesQueryResponse } from 'components/Course/__generated__/queries_HistoricalDelegatesQuery.graphql';

const defaultComponent = () => <div>PlaceHolder Name</div>;

const defaultOption: DropdownOption = {
  id: 1,
  title: 'Test Option',
  component: defaultComponent()
};

const defaultOptions = [defaultOption, defaultOption, defaultOption];

type Props = {
  courses?: CoursesPage_courses;
  router: Router;
};

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '50px 60px'
  },
  course: {
    margin: '15px 0px'
  },
  dropdown: {
    width: 177,
    marginLeft: 15,
    backgroundColor: theme.colors.primaryWhite
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  spaceEvenly: {
    justifyContent: 'space-between'
  },
  filterRow: {
    margin: '30px 0px',
    justifyContent: 'space-between'
  },
  courses: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, 298px)',
    gridGap: 26
  },
  noCourses: {
    fontSize: theme.fontSizes.extraLarge,
    fontWeight: '600',
    color: theme.colors.primaryBlack
  }
}));

const CoursesPageComp = ({ courses, router }: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const [isOpen, setIsOpen] = React.useState(false);

  setBodyOverflow(!isOpen);

  const [selectedCourse, setSelectedCourse] = React.useState<Course>();

  // Set card size depending on course type
  const isOnline = true;

  const [activeDelegates, setActiveDelegates] = React.useState<
    queries_HistoricalDelegatesQueryResponse | undefined
  >();
  const [expiringSoonDelegates, setExpiringSoonDelegates] = React.useState<
    queries_HistoricalDelegatesQueryResponse
  >();

  let _courses: Course[] = [];

  if (courses && courses.edges) {
    _courses = courses.edges.map((course) => ({
      id: course?.course.ident || 1,
      type: course?.course.category?.name || 'cat',
      colour: course?.course.category?.color || 'blue',
      url:
        course?.course.bannerImageURL ||
        '/static/media/SampleImage_ClassroomCoursesDetail_Feat.d89b5773.png',
      title: course?.course.name || 'title',
      price: course?.course.price,
      description: course?.course.excerpt || 'some description',
      assigned: course?.counts.activeTakers,
      expiring: course?.counts.expiringTakers,
      location: 'online',
      date: '29/10/2020',
      modules: course?.counts.modules,
      lessons: course?.counts.lessons,
      video_time: 5
    }));
  }

  // Dropdown states
  const [category, setCategory] = React.useState<DropdownOption>();
  const [itemsPerPage, setItemsPerPage] = React.useState<DropdownOption>();
  // Pagination

  const pageInfo = {
    total: courses?.pageInfo?.total ?? 0,
    limit: courses?.pageInfo?.limit ?? 10,
    offset: courses?.pageInfo?.offset ?? 0
  };
  const page = {
    currentPage: Math.ceil(pageInfo.offset / pageInfo.limit) + 1,
    totalPages: Math.ceil(pageInfo.total / pageInfo.limit)
  };

  const onUpdatePage = ({ offset, limit }: Page) => {
    if (offset <= 0) {
      offset = 1;
    }

    router.push(`/app/courses?offset=${(offset - 1) * limit}&limit=${limit}`);
  };

  return (
    <div className={classes.root}>
      <PageHeader
        title="Courses Available"
        subTitle="All active courses within your organisation"
        sideComponent={
          <CircleBorder text={pageInfo.total} size={30} fontSize={16} />
        }
      />
      <div className={classNames(classes.row, classes.filterRow)}>
        {/* <SelectButton
          selected={isOnline ? 'Online Courses' : 'Classroom Courses'}
          options={['Online Courses', 'Classroom Courses']}
          onClick={(option: string) => ''}
        /> */}
        <div className={classes.row}>
          {/* <div className={classes.dropdown}>
            <Dropdown
              placeholder="Show Categories"
              options={defaultOptions}
              selected={category}
              setSelected={setCategory}
            />
          </div> */}
        </div>
      </div>
      <div className={classNames(classes.courses)}>
        {_courses.length > 0 ? (
          _courses.map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              onClick={() => {
                HistoricalCourseTakers(course.id, { offset: 0, limit: 200 })
                  .then((res) => setActiveDelegates(res))
                  .catch((err) => console.error(err));

                ExpiringSoonDelegates(course.id, 60, { offset: 0, limit: 200 })
                  .then((res) => setExpiringSoonDelegates(res))
                  .catch((err) => console.error(err));

                setIsOpen(true);
                setSelectedCourse(course);
              }}
              size={isOnline ? 'small' : 'large'}
            />
          ))
        ) : (
          <div className={classes.noCourses}>There are no courses to show</div>
        )}
      </div>
      <Spacer vertical spacing={3} />
      <div className={classes.row}>
        <Paginator
          currentPage={page.currentPage}
          updatePage={onUpdatePage}
          numPages={page.totalPages}
          itemsPerPage={pageInfo.limit}
          showRange={page.totalPages}
        />
      </div>
      <Spacer vertical spacing={3} />
      {selectedCourse && (
        <CourseInfo
          course={selectedCourse}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          activeDelegates={activeDelegates}
          expiringSoonDelegates={expiringSoonDelegates}
        />
      )}
    </div>
  );
};

const CoursesPage = createFragmentContainer(CoursesPageComp, {
  courses: graphql`
    fragment CoursesPage_courses on PurchasedCoursePage {
      edges {
        course {
          ident: id
          name
          color
          excerpt
          price
          category {
            name
            color
          }
          bannerImageURL
        }
        counts {
          activeTakers
          expiringTakers
          modules
          lessons
        }
      }
      pageInfo {
        total
        offset
        limit
        given
      }
    }
  `
});

export default CoursesPage;
